// src/components/ConnectionCard.jsx
import { useState, useCallback, useEffect, useRef, memo } from 'react'
import { useDataStore } from '../stores/dataStore'
import { countryFlag } from '../utils/format'
import { playHoverSound, playTypingSound } from '../utils/sounds'
import ActionBar from './ConnectionActions'

const RISK_STYLES = {
  normal:   { bg: 'var(--bg-elevated)',       border: 'var(--border-default)',     accent: 'transparent' },
  info:     { bg: 'rgba(77,157,224,0.04)',     border: 'rgba(77,157,224,0.2)',      accent: 'var(--accent-blue)' },
  warning:  { bg: 'rgba(245,166,35,0.05)',     border: 'rgba(245,166,35,0.25)',     accent: 'var(--accent-amber)' },
  critical: { bg: 'rgba(239,68,68,0.06)',      border: 'rgba(239,68,68,0.3)',       accent: 'var(--accent-red)' },
}

function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="var(--accent-teal)" strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  )
}

const AI_TYPING_INTERVAL_MS = 16

function OllamaLogo({ size = 14 }) {
  return (
    <img
      src="/ollama-logo.png"
      alt=""
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        flexShrink: 0,
        filter: 'drop-shadow(0 0 5px rgba(0, 212, 184, 0.22))',
      }}
    />
  )
}

// Memoized so it only re-renders when conn prop actually changes
const AIExplainPanel = memo(function AIExplainPanel({ conn }) {
  const aiState = useDataStore(s => s.aiExplanations[conn.id])
  const retainConnection = useDataStore(s => s.retainConnection)
  const setAIExplanation = useDataStore(s => s.setAIExplanation)
  const setSelected = useDataStore(s => s.setSelectedConnection)
  const typeRef = useRef(null)
  const fullTextRef = useRef('')
  const mountedRef = useRef(true)

  const status = aiState?.status || 'idle'
  const text = aiState?.text || ''
  const aiResult = aiState?.aiResult || null

  // Typewriter effect with subtle click sound on each character
  const typeOut = useCallback((str) => {
    if (typeRef.current) clearInterval(typeRef.current)
    fullTextRef.current = str
    let i = 0
    setAIExplanation(conn.id, { text: '', fullText: str })
    typeRef.current = setInterval(() => {
      if (i >= str.length) {
        clearInterval(typeRef.current)
        typeRef.current = null
        setAIExplanation(conn.id, { text: str })
        return
      }
      setAIExplanation(conn.id, { text: str.slice(0, i + 1) })
      if (i % 2 === 0) playTypingSound() // Play every 2nd character for more frequent sounds
      i++
    }, AI_TYPING_INTERVAL_MS)
  }, [conn.id, setAIExplanation])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (typeRef.current) {
        clearInterval(typeRef.current)
        typeRef.current = null
        if (fullTextRef.current) setAIExplanation(conn.id, { text: fullTextRef.current })
      }
    }
  }, [conn.id, setAIExplanation])

  const startExplanation = useCallback(async () => {
    if (status === 'loading') return

    retainConnection(conn)
    setSelected(conn.id)
    setAIExplanation(conn.id, { status: 'loading', text: '', fullText: '', aiResult: null })

    try {
      const api = window.electronAPI?.explainConnection
      if (!api) {
        await new Promise(r => setTimeout(r, 1200))
        const demoText = `${conn.appName || 'This app'} is making a ${conn.isEncrypted ? 'secure, encrypted' : 'plain, unencrypted'} connection to ${conn.domain || conn.remoteIP} in ${conn.country || 'an unknown location'} on port ${conn.remotePort}.`
        setAIExplanation(conn.id, { status: 'done', aiResult: null, text: mountedRef.current ? '' : demoText, fullText: demoText })
        if (mountedRef.current) typeOut(demoText)
        return
      }
      const result = await api(conn)
      const resultText = result.text || 'The AI service did not return an explanation.'
      setAIExplanation(conn.id, {
        status: result.ok ? 'done' : 'error',
        aiResult: result,
        text: mountedRef.current ? '' : resultText,
        fullText: resultText,
      })
      if (mountedRef.current) typeOut(resultText)
    } catch {
      const errorText = 'Something went wrong. Try again.'
      setAIExplanation(conn.id, { status: 'error', aiResult: null, text: mountedRef.current ? '' : errorText, fullText: errorText })
      if (mountedRef.current) typeOut(errorText)
    }
  }, [conn, retainConnection, setAIExplanation, setSelected, status, typeOut])

  const handleExplain = useCallback((e) => {
    e.stopPropagation()
    startExplanation()
  }, [startExplanation])

  if (status === 'idle') {
    return (
      <button onClick={handleExplain} style={{
        marginTop: 14, display: 'flex', alignItems: 'center', gap: 7,
        padding: '7px 13px', background: 'rgba(0,212,184,0.08)',
        border: '1px solid rgba(0,212,184,0.25)', borderRadius: 8,
        color: 'var(--accent-teal)', fontSize: 12, fontWeight: 500,
        cursor: 'pointer', width: 'fit-content', fontFamily: 'var(--font-body)',
        transition: 'background 0.15s, border-color 0.15s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background='rgba(0,212,184,0.15)'; e.currentTarget.style.borderColor='rgba(0,212,184,0.45)' }}
        onMouseLeave={e => { e.currentTarget.style.background='rgba(0,212,184,0.08)'; e.currentTarget.style.borderColor='rgba(0,212,184,0.25)' }}
      >
        <OllamaLogo size={14} />
        Ask AI to explain this
      </button>
    )
  }

  if (status === 'loading') {
    return (
      <div style={{
        marginTop: 14, padding: '11px 13px', background: 'rgba(0,212,184,0.05)',
        border: '1px solid rgba(0,212,184,0.2)', borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 9,
        fontSize: 12, color: 'var(--text-secondary)',
      }}>
        <Spinner />
        <span>Asking AI to explain this connection…</span>
      </div>
    )
  }

  return (
    <div onClick={e => e.stopPropagation()} style={{
      marginTop: 14, padding: '12px 14px',
      background: status === 'error' ? 'rgba(239,68,68,0.05)' : 'rgba(0,212,184,0.05)',
      border: `1px solid ${status === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(0,212,184,0.2)'}`,
      borderRadius: 8,
    }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
        <div style={{
          display:'flex', alignItems:'center', gap: 6, fontSize: 10,
          fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em',
          color: status === 'error' ? 'var(--accent-red)' : 'var(--accent-teal)',
        }}>
          {status === 'error'
            ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            : <OllamaLogo size={12} />
          }
          {status === 'error' ? 'AI Error' : aiResult?.provider === 'ollama' ? `AI Explanation · Ollama (${aiResult.model || 'local'})` : 'AI Explanation · llama 3.2'}
        </div>
        <button onClick={handleExplain} style={{
          background:'none', border:'none', cursor:'pointer', fontSize: 10,
          color:'var(--text-tertiary)', fontFamily:'var(--font-mono)',
          padding:'2px 6px', borderRadius: 4, transition:'color 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.color='var(--text-primary)'}
          onMouseLeave={e => e.currentTarget.style.color='var(--text-tertiary)'}
        >ask again ↺</button>
      </div>
      <p style={{ fontSize:13, color:'var(--text-primary)', lineHeight:1.65, margin:0, fontWeight:300 }}>
        {text}
      </p>
    </div>
  )
})

// Memoized card — only re-renders if this specific connection's data changed
const ConnectionCard = memo(function ConnectionCard({ connection: conn, index = 0 }) {
  const isSelected = useDataStore(s => s.selectedConnectionId === conn.id)
  const setSelected = useDataStore(s => s.setSelectedConnection)
  const releaseRetainedConnection = useDataStore(s => s.releaseRetainedConnection)
  const isRetained = Boolean(conn.isRetained)
  const [expanded, setExpanded] = useState(() => isRetained || isSelected)

  const style = RISK_STYLES[conn.riskLevel] || RISK_STYLES.normal
  const flag = countryFlag(conn.countryCode)

  useEffect(() => {
    if (isRetained || isSelected) setExpanded(true)
  }, [isRetained, isSelected])

  const handleClick = useCallback(() => {
    setSelected(isSelected ? null : conn.id)
    setExpanded(e => !e)
  }, [isSelected, conn.id, setSelected])

  return (
    <div className="animate-fade-in" style={{
      borderRadius: 12,
      border: `1px solid ${isSelected ? 'rgba(77,157,224,0.4)' : style.border}`,
      background: style.bg,
      transition: 'border-color 0.15s, background 0.15s',
      animationDelay: `${Math.min(index, 7) * 40}ms`,
      animationFillMode: 'both',
    }}>

      {/* Clickable header row */}
      <div style={{ cursor: 'pointer', borderRadius: expanded ? '12px 12px 0 0' : 12, overflow: 'hidden' }}
        onClick={handleClick}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
          playHoverSound()
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent'
        }}
      >

        {conn.riskLevel !== 'normal' && (
          <div style={{ height: 2, background: style.accent, opacity: 0.7 }} />
        )}

        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px' }}>
          <div style={{
            width:32, height:32, borderRadius:8, flexShrink:0,
            background: conn.isEncrypted ? 'rgba(62,207,142,0.1)' : 'rgba(245,166,35,0.1)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            {conn.isEncrypted
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            }
          </div>

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)', marginBottom:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {conn.summary}
            </div>
            <div style={{
              display:'flex',
              alignItems:'center',
              fontSize:11,
              color:'var(--text-tertiary)',
              fontFamily:'var(--font-mono)',
            }}>
              {isRetained && (
                <span style={{
                  flexShrink:0, color:'var(--accent-teal)',
                  padding:'1px 5px', borderRadius:4,
                  background:'rgba(0,212,184,0.08)',
                  border:'1px solid rgba(0,212,184,0.18)',
                }}>Connection ended</span>
              )}
              <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{flag} {conn.country}</span>
            </div>
          </div>

          <div style={{ fontSize:10, color:'var(--text-tertiary)', transition:'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink:0 }}>▼</div>
        </div>
      </div>

      {expanded && (
        <div style={{
          borderTop: '1px solid var(--border-subtle)',
          padding: '14px 14px 16px',
          background: 'rgba(0,0,0,0.25)',
          borderRadius: '0 0 12px 12px',
        }}
          onClick={e => e.stopPropagation()}
        >
          {isRetained && (
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
              marginBottom:12, padding:'8px 10px', borderRadius:8,
              background:'rgba(0,212,184,0.05)',
              border:'1px solid rgba(0,212,184,0.16)',
              color:'var(--text-secondary)', fontSize:11, lineHeight:1.4,
            }}>
              <span>Connection ended. The AI explanation stays here.</span>
              <button onClick={() => releaseRetainedConnection(conn.id)} style={{
                background:'none', border:'none', cursor:'pointer',
                color:'var(--accent-teal)', fontSize:11,
                fontFamily:'var(--font-body)', padding:'2px 4px',
              }}>Clear</button>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 14 }}>
            <DetailItem label="Remote IP"    value={conn.remoteIP}            mono />
            <DetailItem label="Port"         value={conn.remotePort}          mono />
            <DetailItem label="Protocol"     value={conn.protocol}            mono />
            <DetailItem label="Domain"       value={conn.domain || 'Unknown'} mono />
            <DetailItem label="Country"      value={`${flag} ${conn.country}`}     />
            <DetailItem label="App PID"      value={conn.pid}                 mono />
          </div>
          <ActionBar conn={conn} allowKill={!isRetained} />
          <AIExplainPanel conn={conn} />
        </div>
      )}
    </div>
  )
})

export default ConnectionCard

function DetailItem({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize:10, color:'var(--text-tertiary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>
        {label}
      </div>
      <div style={{ fontSize:12, color:'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : 'inherit', wordBreak:'break-all', lineHeight: 1.4 }}>
        {value ?? '—'}
      </div>
    </div>
  )
}
